import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@/lib/logger';

const DYNAMIC_IMPORT_RELOAD_KEY_PREFIX = 'sopho:dynamic-import-reload:';

function isDynamicImportError(error: Error): boolean {
  const haystack = `${error.name} ${error.message}`.toLowerCase();
  return haystack.includes('failed to fetch dynamically imported module')
    || haystack.includes('importing a module script failed')
    || haystack.includes('chunkloaderror');
}

function getDynamicImportReloadKey(pathname: string): string {
  return `${DYNAMIC_IMPORT_RELOAD_KEY_PREFIX}${pathname}`;
}

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  hasError: boolean;
};

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('AppErrorBoundary', 'Unhandled UI error', { error, errorInfo });

    if (typeof window === 'undefined' || !isDynamicImportError(error)) {
      return;
    }

    const reloadKey = getDynamicImportReloadKey(window.location.pathname);
    if (window.sessionStorage.getItem(reloadKey)) {
      return;
    }

    window.sessionStorage.setItem(reloadKey, '1');
    window.location.reload();
  }

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(getDynamicImportReloadKey(window.location.pathname));
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-6 py-16">
          <div className="max-w-md space-y-4 rounded-2xl border border-border bg-card p-8 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Application Error
            </p>
            <h1 className="text-2xl font-semibold text-foreground">
              Die Anwendung konnte diese Ansicht nicht laden.
            </h1>
            <p className="text-sm leading-6 text-muted-foreground">
              Der Fehler wurde im Browser protokolliert. Ein Neuladen stellt den Zustand in den meisten Fällen wieder her.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background transition-opacity hover:opacity-90"
            >
              Neu laden
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}